import { inject, Injectable } from "@angular/core";
import { injectQuery, queryOptions } from "@tanstack/angular-query-experimental";
import { lastValueFrom } from "rxjs";
import { filter, finalize, first, switchMap } from "rxjs/operators";

import { AWSHttpClient } from "@intocare/aws";
import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";
import { autoSubscribe } from "@intocare/rxjs";
import { ModalService } from "@intocare/ui";

import { ChangelogComponent } from "../components/changelog/changelog.component";
import { WindowRef } from "../tokens/window-ref";

@Injectable({
	providedIn: "root",
})
export class ChangelogService {
	private window = inject<Window>(WindowRef);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	private readonly modalService = inject(ModalService);
	private readonly http = inject(AWSHttpClient);
	private readonly checkClientQuery = injectQuery(() =>
		queryOptions({
			queryKey: ["client-check"],
			queryFn: () => lastValueFrom(this.checkClient()),
			staleTime: Infinity,
			throwOnError: false,
			retry: 0,
		}),
	);

	checkClient() {
		return this.http.get(`${this.environment.API.url}/client-check`, {
			params: {
				"user-agent": encodeURIComponent(this.window.navigator.userAgent),
			},
		});
	}

	loadChangelog() {
		return this.http.get("changelog", { responseType: "text" }).pipe(
			filter(Boolean),
			switchMap((response: string) => {
				const modalRef = this.modalService.open(ChangelogComponent, {
					backdrop: true,
					disableClose: true,
					width: 700,
					height: 700,
				});

				modalRef.instance.changelog = response;
				return modalRef.instance.closeItem.pipe(
					first(),
					finalize(() => modalRef.close()),
				);
			}),
			autoSubscribe(),
		);
	}
}
